import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Library, Sparkles, FileSignature, MapPin, ChevronRight, ChevronLeft, X, Truck } from 'lucide-react';
import { PROVINCES } from '../lib/taxService';
import { getLicenceProvider } from '../lib/licenceProvider';
import { useT } from '../lib/i18n';

const ONBOARDING_KEY = 'soumissionpro_onboarding_done_v2';
const LEGACY_ONBOARDING_KEY = 'soumissionpro_onboarding_done_v1';
const PROVINCE_HINT_KEY = 'soumissionpro_onboarding_province';

export function hasSeenOnboarding() {
  try {
    // Clean up the legacy flag if it exists so we don't carry it forever.
    if (window.localStorage.getItem(LEGACY_ONBOARDING_KEY)) {
      window.localStorage.removeItem(LEGACY_ONBOARDING_KEY);
    }
    return window.localStorage.getItem(ONBOARDING_KEY) === '1';
  } catch {
    return true; // Fail safe: never block the app
  }
}

export function markOnboardingDone() {
  try {
    window.localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Returns the province code the user picked during onboarding (if any).
 * Consumed by DataContext on the first profile save after signup.
 */
export function getOnboardingProvinceHint() {
  try {
    return window.localStorage.getItem(PROVINCE_HINT_KEY) || null;
  } catch {
    return null;
  }
}

export function clearOnboardingProvinceHint() {
  try {
    window.localStorage.removeItem(PROVINCE_HINT_KEY);
  } catch {
    /* ignore */
  }
}

// All onboarding copy lives in a bilingual dictionary so the screen reads in
// the user's preferred language from the very first interaction.
const STR = {
  fr: {
    skip: 'Passer',
    prev_aria: 'Précédent',
    continue_btn: 'Continuer',
    start_trial_btn: 'Démarrer mon essai gratuit',
    no_card_footer: '3 jours gratuits · Aucune carte requise',
    province_eyebrow: '05 / 05',
    province_label: 'TA PROVINCE',
    province_title: 'Tu travailles où principalement?',
    province_desc_pre: 'On adapte les ',
    province_desc_taxes: 'taxes',
    province_desc_mid1: ' (TPS, TVQ, TVH), les ',
    province_desc_prices: 'prix par région',
    province_desc_mid2: ' et la ',
    province_desc_verif: 'vérification de licence',
    province_desc_post: ' (RBQ, HCRA, BC Housing, etc.) selon ta province.',
    province_select_label: 'Sélectionne ta province',
    preview_field_short: 'Numéro de licence',
    preview_field_long: "Numéro de licence d'entrepreneur",
    preview_field_with: (s) => `Numéro de licence ${s}`,
    preview_open_registry: (name) => `Ouvrir le registre ${name}`,
    preview_with_registry_pre: 'Ton champ licence affichera ',
    preview_with_registry_mid: ' et un bouton ',
    preview_with_registry_post: '.',
    preview_no_registry: "Cette province n'a pas de registre public centralisé — un message t'expliquera où vérifier ta licence (municipalité, WCB, etc.).",
    slides: [
      {
        id: 'catalog',
        label: 'CATALOGUE CANADIEN',
        title: '1500+ matériaux\ndéjà dans ta poche.',
        body: 'Bois, plomberie, électricité, béton, toiture, peinture… Avec leurs prix de référence canadiens. Tu choisis, tu ajoutes, c\'est tout.',
      },
      {
        id: 'ai',
        label: 'INTELLIGENCE ARTIFICIELLE',
        title: 'Tes prix ajustés\npour ta région.',
        body: "L'IA Claude analyse le type de chantier, ta province et le marché 2026 pour proposer le bon prix. Trois-Rivières, Toronto, Vancouver — adapté à ton coin.",
      },
      {
        id: 'suppliers',
        label: 'PRIX FOURNISSEURS · GAME CHANGER',
        title: 'Importe les prix\nde tes fournisseurs.',
        body: "Upload la liste Excel, PDF ou prends une photo de la liste papier de ton fournisseur. L'IA extrait TES prix négociés (BMR, Réno-Dépôt…) pour les utiliser dans tes soumissions. Inclus dans tous les plans, même en essai gratuit.",
      },
      {
        id: 'signature',
        label: 'SIGNATURE NUMÉRIQUE',
        title: 'Ton client signe\nsur son téléphone.',
        body: "Envoie le lien par texto ou courriel. Il signe en 10 secondes, c'est légal, c'est tracé, c'est encaissable. Plus jamais de papier.",
      },
    ],
  },
  en: {
    skip: 'Skip',
    prev_aria: 'Previous',
    continue_btn: 'Continue',
    start_trial_btn: 'Start my free trial',
    no_card_footer: '3 days free · No card required',
    province_eyebrow: '05 / 05',
    province_label: 'YOUR PROVINCE',
    province_title: 'Where do you mainly work?',
    province_desc_pre: 'We adapt ',
    province_desc_taxes: 'taxes',
    province_desc_mid1: ' (GST, QST, HST), ',
    province_desc_prices: 'regional pricing',
    province_desc_mid2: ' and ',
    province_desc_verif: 'licence verification',
    province_desc_post: ' (RBQ, HCRA, BC Housing, etc.) for your province.',
    province_select_label: 'Select your province',
    preview_field_short: 'Licence',
    preview_field_long: 'Contractor licence number',
    preview_field_with: (s) => `${s} licence number`,
    preview_open_registry: (name) => `Open the ${name} registry`,
    preview_with_registry_pre: 'Your licence field will display ',
    preview_with_registry_mid: ' and a button ',
    preview_with_registry_post: '.',
    preview_no_registry: "This province doesn't have a centralized public registry — a message will explain where to verify your licence (municipality, WCB, etc.).",
    slides: [
      {
        id: 'catalog',
        label: 'CANADIAN CATALOG',
        title: '1500+ materials\nin your pocket.',
        body: 'Lumber, plumbing, electrical, concrete, roofing, paint… With Canadian reference prices. You pick, you add, that\'s it.',
      },
      {
        id: 'ai',
        label: 'ARTIFICIAL INTELLIGENCE',
        title: 'Pricing tuned\nto your region.',
        body: 'Claude AI analyzes the project type, your province and the 2026 market to suggest the right price. Trois-Rivières, Toronto, Vancouver — adapted to your area.',
      },
      {
        id: 'suppliers',
        label: 'SUPPLIER PRICES · GAME CHANGER',
        title: 'Import your\nsuppliers\' prices.',
        body: "Upload Excel, PDF or snap a photo of your supplier's paper price list. The AI extracts YOUR negotiated prices (BMR, Réno-Dépôt…) to use in your quotes. Included in all plans, even during the free trial.",
      },
      {
        id: 'signature',
        label: 'DIGITAL SIGNATURE',
        title: 'Your client signs\non their phone.',
        body: "Send the link by SMS or email. They sign in 10 seconds — legal, tracked, payable. No more paper.",
      },
    ],
  },
};

const SLIDE_META = [
  { Icon: Library,       accent: '#C19A57', iconColor: '#C19A57', eyebrow: '01 / 05' },
  { Icon: Sparkles,      accent: '#7A1F2B', iconColor: '#FAF7F2', eyebrow: '02 / 05' },
  { Icon: Truck,         accent: '#C19A57', iconColor: '#C19A57', eyebrow: '03 / 05' },
  { Icon: FileSignature, accent: '#C19A57', iconColor: '#C19A57', eyebrow: '04 / 05' },
];

const TOTAL = SLIDE_META.length + 1; // +1 for the final province-picker slide

export default function Onboarding() {
  const navigate = useNavigate();
  const { lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const [index, setIndex] = useState(0);
  const [province, setProvince] = useState(() => {
    try {
      return window.localStorage.getItem(PROVINCE_HINT_KEY) || 'QC';
    } catch {
      return 'QC';
    }
  });
  const touchStartXRef = useRef(null);
  const touchEndXRef = useRef(null);

  const isProvinceSlide = index === SLIDE_META.length;

  const persistProvince = (code) => {
    setProvince(code);
    try {
      window.localStorage.setItem(PROVINCE_HINT_KEY, code);
    } catch { /* ignore */ }
  };

  const finish = () => {
    persistProvince(province); // ensure the choice (or default) is stored
    markOnboardingDone();
    navigate('/auth', { replace: true });
  };

  const next = () => {
    if (index < TOTAL - 1) setIndex(index + 1);
    else finish();
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const onTouchStart = (e) => {
    touchStartXRef.current = e.changedTouches[0].clientX;
    touchEndXRef.current = null;
  };
  const onTouchMove = (e) => {
    touchEndXRef.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (touchStartXRef.current == null || touchEndXRef.current == null) return;
    const delta = touchStartXRef.current - touchEndXRef.current;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) next();
    else prev();
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  const slideMeta = !isProvinceSlide ? SLIDE_META[index] : null;
  const slideCopy = !isProvinceSlide ? T.slides[index] : null;
  const lp = isProvinceSlide ? getLicenceProvider(province) : null;

  // Compute the short licence label preview (matches Profile.jsx wording).
  const previewLicenceLabel = lp && lp.short === 'Licence'
    ? T.preview_field_long
    : lp ? T.preview_field_with(lp.short) : '';

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FAF7F2' }}
      data-testid="onboarding-page"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-6 pb-2">
        <div className="text-xs font-black tracking-widest" style={{ color: '#7A1F2B' }}>
          SOUMISSIONPRO
        </div>
        {/* "Skip" hidden on the province slide → province choice is mandatory */}
        {!isProvinceSlide ? (
          <button
            type="button"
            onClick={finish}
            data-testid="onboarding-skip"
            className="text-xs font-bold flex items-center gap-1 px-3 py-1.5 rounded-full"
            style={{ color: '#6B7280' }}
          >
            {T.skip} <X size={14} />
          </button>
        ) : (
          <span className="w-12" /> /* keep header layout balanced */
        )}
      </header>

      {/* Slide content */}
      <main className="flex-1 flex flex-col justify-center px-7 py-6">
        <div className="max-w-md w-full mx-auto">
          {!isProvinceSlide ? (
            <>
              {/* === INFO slide === */}
              <div className="mb-10 relative" key={slideCopy.id}>
                <div
                  className="w-24 h-24 rounded-3xl flex items-center justify-center"
                  style={{
                    backgroundColor: '#7A1F2B',
                    color: slideMeta.iconColor || slideMeta.accent,
                    boxShadow: '0 30px 60px -25px rgba(122, 31, 43, 0.55)',
                    animation: 'onboardingPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                >
                  <slideMeta.Icon size={42} strokeWidth={2.2} />
                </div>
                <div
                  className="absolute -top-3 -left-3 w-10 h-10 rounded-full border-2"
                  style={{ borderColor: slideMeta.accent, opacity: 0.6 }}
                />
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="text-[10px] font-black tracking-[0.3em]" style={{ color: '#7A1F2B' }}>
                  {slideMeta.eyebrow}
                </div>
                <div className="h-px flex-1" style={{ backgroundColor: '#E5DCC9' }} />
                <div className="text-[10px] font-black tracking-[0.2em]" style={{ color: slideMeta.accent }}>
                  {slideCopy.label}
                </div>
              </div>

              <h1
                className="font-heading text-4xl sm:text-5xl font-black mb-5 leading-[1.05] tracking-tight whitespace-pre-line"
                style={{ color: '#1F1F1F', animation: 'onboardingSlideUp 0.6s ease-out 0.05s both' }}
              >
                {slideCopy.title}
              </h1>

              <p
                className="text-base leading-relaxed mb-10"
                style={{ color: '#4A5568', animation: 'onboardingSlideUp 0.6s ease-out 0.15s both' }}
              >
                {slideCopy.body}
              </p>
            </>
          ) : (
            <>
              {/* === PROVINCE slide === */}
              <div className="mb-10 relative">
                <div
                  className="w-24 h-24 rounded-3xl flex items-center justify-center"
                  style={{
                    backgroundColor: '#7A1F2B',
                    color: '#C19A57',
                    boxShadow: '0 30px 60px -25px rgba(122, 31, 43, 0.55)',
                    animation: 'onboardingPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                >
                  <MapPin size={42} strokeWidth={2.2} />
                </div>
                <div
                  className="absolute -top-3 -left-3 w-10 h-10 rounded-full border-2"
                  style={{ borderColor: '#C19A57', opacity: 0.6 }}
                />
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="text-[10px] font-black tracking-[0.3em]" style={{ color: '#7A1F2B' }}>
                  {T.province_eyebrow}
                </div>
                <div className="h-px flex-1" style={{ backgroundColor: '#E5DCC9' }} />
                <div className="text-[10px] font-black tracking-[0.2em]" style={{ color: '#7A1F2B' }}>
                  {T.province_label}
                </div>
              </div>

              <h1
                className="font-heading text-4xl sm:text-5xl font-black mb-3 leading-[1.05] tracking-tight"
                style={{ color: '#1F1F1F', animation: 'onboardingSlideUp 0.6s ease-out 0.05s both' }}
              >
                {T.province_title}
              </h1>

              <p
                className="text-base leading-relaxed mb-6"
                style={{ color: '#4A5568', animation: 'onboardingSlideUp 0.6s ease-out 0.15s both' }}
              >
                {T.province_desc_pre}
                <b>{T.province_desc_taxes}</b>
                {T.province_desc_mid1}
                <b>{T.province_desc_prices}</b>
                {T.province_desc_mid2}
                <b>{T.province_desc_verif}</b>
                {T.province_desc_post}
              </p>

              <label
                htmlFor="onboarding-province"
                className="text-[11px] font-black tracking-[0.2em] uppercase mb-2 block"
                style={{ color: '#6B7280' }}
              >
                {T.province_select_label}
              </label>
              <select
                id="onboarding-province"
                data-testid="onboarding-province-select"
                value={province}
                onChange={(e) => persistProvince(e.target.value)}
                className="w-full py-4 px-4 text-base font-bold rounded-2xl border-2 mb-4"
                style={{ borderColor: '#7A1F2B', color: '#1F1F1F', backgroundColor: '#FFFFFF' }}
              >
                {PROVINCES.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code} — {p.name}
                  </option>
                ))}
              </select>

              {/* Live preview of what the licence label will be */}
              {lp && (
                <div
                  className="p-3 rounded-xl border text-xs flex items-start gap-2 mb-6"
                  style={{ backgroundColor: '#FAF7F2', borderColor: '#E5DCC9', color: '#4A5568' }}
                  data-testid="onboarding-province-preview"
                >
                  <span style={{ color: '#7A1F2B', fontSize: 14, lineHeight: 1 }}>✓</span>
                  <span>
                    {lp.hasRegistry ? (
                      <>
                        {T.preview_with_registry_pre}
                        <b>« {previewLicenceLabel} »</b>
                        {T.preview_with_registry_mid}
                        <b>« {T.preview_open_registry(lp.registryName)} »</b>
                        {T.preview_with_registry_post}
                      </>
                    ) : (
                      T.preview_no_registry
                    )}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Pagination dots */}
          <div className="flex items-center gap-2 mb-8">
            {Array.from({ length: TOTAL }).map((_, i) => (
              <div
                key={`dot-${i}`}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === index ? 28 : 8,
                  backgroundColor: i === index ? '#7A1F2B' : '#E5DCC9',
                }}
              />
            ))}
          </div>
        </div>
      </main>

      {/* Footer nav */}
      <footer className="px-5 pb-8 pt-2 max-w-md w-full mx-auto">
        <div className="flex items-center gap-3">
          {index > 0 && (
            <button
              type="button"
              onClick={prev}
              data-testid="onboarding-prev"
              className="w-14 h-14 rounded-2xl flex items-center justify-center border-2 active:scale-95 transition-transform"
              style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}
              aria-label={T.prev_aria}
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <button
            type="button"
            onClick={next}
            data-testid={isProvinceSlide ? 'onboarding-finish' : 'onboarding-next'}
            className="flex-1 h-14 rounded-2xl font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            style={{
              backgroundColor: '#7A1F2B',
              color: '#FFFFFF',
              boxShadow: '0 20px 40px -20px rgba(122, 31, 43, 0.6)',
            }}
          >
            {isProvinceSlide ? T.start_trial_btn : T.continue_btn}
            <ChevronRight size={20} />
          </button>
        </div>
        {!isProvinceSlide && (
          <p className="text-center text-xs mt-4" style={{ color: '#9CA3AF' }}>
            {T.no_card_footer}
          </p>
        )}
      </footer>

      <style>{`
        @keyframes onboardingPop {
          0%   { opacity: 0; transform: scale(0.6) rotate(-8deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes onboardingSlideUp {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
